import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ChevronLeft, Plus, Trash } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EQUIPMENT_VALUES } from "@/lib/constants";
import {
  createEquipmentFormSchema,
  EquipmentCreateRequest,
} from "@/api/create-equipment";
import { useCreateEquipment } from "@/hooks";
import { useState } from "react";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
  TableHead,
} from "@/components/ui/table";

export default function CreateEquipmentPage() {
  const navigate = useNavigate();
  const createEquipment = useCreateEquipment();
  const createEquipmentForm = useForm<EquipmentCreateRequest>({
    resolver: zodResolver(createEquipmentFormSchema),
  });

  const [piecewiseIntervals, setPiecewiseIntervals] = useState<
    Array<{ start: number; end: number; offset: number; scalar: number }>
  >([{ start: 0, end: 100, offset: 0, scalar: 1 }]);

  return (
    <div className="container mx-auto pb-8 grid gap-3">
      <div className="flex items-center gap-0">
        <Link
          to="/equipos"
          className="flex items-center gap-2 text-sm text-muted-foreground"
        >
          <ChevronLeft className="size-4" />
          Regresar
        </Link>
      </div>
      <div>
        <h1 className="text-lg font-semibold">Crear Equipo</h1>
        <p className="text-xs text-muted-foreground">
          Agrega un nuevo equipo para que puedas registrar sus calibraciones y
          mantenimientos.
        </p>
      </div>
      <Separator />
      <Form {...createEquipmentForm}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={createEquipmentForm.control}
            name="typeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo de Equipo</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Seleccione un tipo de equipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {EQUIPMENT_VALUES.map((value) => (
                        <SelectItem key={value.value} value={value.value}>
                          {value.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={createEquipmentForm.control}
            name="serialNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Número de Serie</FormLabel>
                <FormControl>
                  <Input placeholder="Ej. ABCX123456" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={createEquipmentForm.control}
            name="brand"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Marca</FormLabel>
                <FormControl>
                  <Input placeholder="Ej. Extech" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={createEquipmentForm.control}
            name="model"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Modelo</FormLabel>
                <FormControl>
                  <Input placeholder="Ej. LXT200" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={createEquipmentForm.control}
            name="calibratedAt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fecha de Calibración</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={createEquipmentForm.control}
            name="nextCalibration"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Próxima Calibración</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={createEquipmentForm.control}
          name="certificate"
          render={({ field: { onChange } }) => (
            <FormItem>
              <FormLabel>Certificado</FormLabel>
              <FormControl>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) =>
                    onChange(e.target.files ? e.target.files[0] : null)
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {createEquipmentForm.watch("typeId") === "LlcbnmHu" && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="enableCalibrationCurve"
              checked={
                createEquipmentForm.watch("enableCalibrationCurve") || false
              }
              onCheckedChange={(checked) => {
                createEquipmentForm.setValue(
                  "enableCalibrationCurve",
                  checked === true
                );
              }}
            />
            <Label htmlFor="enableCalibrationCurve">Incluir Correcciónes</Label>
          </div>
        )}

        {createEquipmentForm.watch("enableCalibrationCurve") &&
          createEquipmentForm.watch("typeId") === "LlcbnmHu" && (
            <>
              <Button
                type="button"
                variant="success"
                size="sm"
                className="gap-2 w-min justify-self-end"
                onClick={() => {
                  setPiecewiseIntervals([
                    ...piecewiseIntervals,
                    {
                      start:
                        piecewiseIntervals[piecewiseIntervals.length - 1].end,
                      end:
                        piecewiseIntervals[piecewiseIntervals.length - 1].end +
                        100,
                      offset: 0,
                      scalar: 1,
                    },
                  ]);
                }}
              >
                <Plus className="size-4" />
                Agregar intervalo
              </Button>
              <Table>
                <TableHeader>
                  <TableRow className="text-sm tracking-tight">
                    <TableHead>
                      <p>Minimo</p>
                      <p>(lux)</p>
                    </TableHead>
                    <TableHead>
                      <p>Maximo</p>
                      <p>(lux)</p>
                    </TableHead>
                    <TableHead>
                      <p>Corrección </p>
                      <p>(Aditiva)</p>
                    </TableHead>
                    <TableHead>
                      <p>Corrección </p>
                      <p>(Multiplicativa)</p>
                    </TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                {piecewiseIntervals.map((interval, index) => (
                  <TableRow key={index} className="p-0 m-0 font-mono">
                    <TableCell className="p-0 m-0">
                      <Input
                        id={`interval-${index}-start`}
                        type="number"
                        value={interval.start}
                        className="w-full h-full border-none shadow-none"
                        onChange={(e) => {
                          const newIntervals = [...piecewiseIntervals];
                          newIntervals[index].start = Number(e.target.value);
                          setPiecewiseIntervals(newIntervals);
                        }}
                      />
                    </TableCell>
                    <TableCell className="p-0 m-0">
                      <Input
                        id={`interval-${index}-end`}
                        type="number"
                        value={interval.end}
                        onChange={(e) => {
                          const newIntervals = [...piecewiseIntervals];
                          newIntervals[index].end = Number(e.target.value);
                          setPiecewiseIntervals(newIntervals);
                        }}
                        className="w-full h-full border-none shadow-none"
                      />
                    </TableCell>
                    <TableCell className="p-0 m-0">
                      <Input
                        id={`interval-${index}-offset`}
                        type="number"
                        value={interval.offset}
                        onChange={(e) => {
                          const newIntervals = [...piecewiseIntervals];
                          newIntervals[index].offset = Number(e.target.value);
                          setPiecewiseIntervals(newIntervals);
                        }}
                        className="w-full h-full border-none shadow-none"
                      />
                    </TableCell>
                    <TableCell className="p-0 m-0">
                      <Input
                        id={`interval-${index}-scalar`}
                        type="number"
                        value={interval.scalar}
                        onChange={(e) => {
                          const newIntervals = [...piecewiseIntervals];
                          newIntervals[index].scalar = Number(e.target.value);
                          setPiecewiseIntervals(newIntervals);
                        }}
                        className="w-full h-full border-none shadow-none"
                      />
                    </TableCell>
                    <TableCell className="p-0 m-0">
                      <Button
                        type="button"
                        disabled={piecewiseIntervals.length < 2}
                        variant="ghost"
                        className="hover:bg-transparent focus:bg-transparent hover:cursor-pointer"
                        size="icon"
                        onClick={() => {
                          const newIntervals = [...piecewiseIntervals];
                          newIntervals.splice(index, 1);
                          setPiecewiseIntervals(newIntervals);
                        }}
                      >
                        <Trash className="size-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </Table>
            </>
          )}

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="destructive" type="button" asChild>
            <Link to="/equipos">Cancelar</Link>
          </Button>
          <Button
            type="submit"
            disabled={createEquipment.isPending}
            onClick={createEquipmentForm.handleSubmit(async (data) => {
              if (data.enableCalibrationCurve) {
                data.calibrationIntervals = piecewiseIntervals;
              }

              await createEquipment.mutateAsync(data);
              navigate("/equipos");
            })}
          >
            {createEquipment.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin mr-2" />
                Guardando...
              </>
            ) : (
              "Guardar equipo"
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
}
